import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import RFG from "../../libs/inputs/RFG";
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Stack } from '@mui/material';
import MultiSelect from "../extra/MultiSelect";
import CountrySelect from "../extra/CountrySelect";
import { seedData } from "../../libs/Libs";
import { FormHeroHeader, FormSectionCard, FormFooter, labelStyle } from "../extra/FormLayout";
import { BsPersonBadgeFill } from "react-icons/bs";
import { generateFakeUserData } from "../../libs/testDataGenerator";

export default function Form({
    title,
    roles = [],
    users = [],
    defaultValues = {},
    onSubmit = () => { },
    onChange = () => { },
    back = () => { },
}) {
    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues,
        mode: "all",
        shouldFocusError: true,
    });

    const initialRole = React.useMemo(() => {
        if (Array.isArray(defaultValues?.group) && defaultValues.group.length > 0) {
            return defaultValues.group.map(r => typeof r === 'string' ? r : (r?.name || '')).filter(Boolean);
        }
        if (typeof defaultValues?.group === 'string' && defaultValues.group) {
            return [defaultValues.group];
        }
        if (defaultValues?.main_role && defaultValues.main_role !== 'User') {
            return [defaultValues.main_role];
        }
        return defaultValues?.main_role ? [defaultValues.main_role] : ["Trainee"];
    }, [defaultValues]);

    useEffect(() => {
        if (initialRole && initialRole.length > 0) {
            setValue("group", initialRole);
        }
    }, [initialRole, setValue]);

    const handleFormSubmit = (data) => {
        console.log("GROUP", data)
        onSubmit(data);
    };

    const handleFillFakeData = () => {
        const fakeData = generateFakeUserData(roles);
        Object.entries(fakeData).forEach(([key, value]) => {
            setValue(key, value, { shouldValidate: true });
        });
        clearErrors();
    };

    const showAutoFill = process.env.REACT_APP_DEBUG !== "false" && process.env.REACT_APP_DEBUG !== "False" && process.env.REACT_APP_DEBUG !== "0";

    return (
        <Box sx={{ py: 3, px: { xs: 1, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
            <FormHeroHeader
                icon={<BsPersonBadgeFill size={26} />}
                title={title || "User Profile Setup"}
                subtitle="Configure employee personal details, contact numbers, login username, and assigned access roles."
                badgeText="User Record"
            />
            <Box display="flex" justifyContent="flex-end" mb={2.5}>
                {showAutoFill && (
                    <Button
                        data-testid="btn-auto-fill-fake-user"
                        type="button"
                        variant="outlined"
                        onClick={handleFillFakeData}
                        sx={{
                            borderColor: '#10b981',
                            color: '#059669',
                            bgcolor: '#ecfdf5',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '13px',
                            borderRadius: '20px',
                            px: 2.5, py: 0.8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            boxShadow: '0 2px 6px rgba(16, 185, 129, 0.15)',
                            '&:hover': { bgcolor: '#d1fae5', borderColor: '#059669', transform: 'translateY(-1px)' }
                        }}
                    >
                        ⚡ Auto-Fill Sample Data
                    </Button>
                )}
            </Box>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <FormSectionCard
                    icon={<BsPersonBadgeFill size={20} />}
                    title="Employee Credentials & Access Role"
                    subtitle="Set name, phone contact, email username, and system permissions"
                >
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2.5}>
                                <RFG name="first_name" label="First Name *" control={control} labelStyle={labelStyle}
                                    rules={{
                                        required: true, maxLength: 100
                                    }}>
                                    <input autoComplete="off" placeholder="e.g. John" />
                                </RFG>
                                <RFG name="last_name" label="Last Name *" control={control} labelStyle={labelStyle}
                                    rules={{
                                        required: true, maxLength: 100
                                    }}>
                                    <input autoComplete="off" placeholder="e.g. Doe" />
                                </RFG>
                                <Box display={'flex'} gap={2}>
                                    <Box minWidth={110}>
                                        <RFG
                                            name="country_code"
                                            label="Phone Code *"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{ required: true }}
                                        >
                                            <CountrySelect
                                                defaultValue={defaultValues.country_code}
                                                labelKey="dial_code"
                                                valueKey="dial_code"
                                                onValueChange={(country) => {
                                                    const value = country?.value || "";
                                                    setValue("country_code", value, { shouldValidate: true });
                                                    if (value) clearErrors("country_code");
                                                }}
                                            />
                                        </RFG>
                                    </Box>
                                    <Box width={1}>
                                        <RFG
                                            name="phone"
                                            label="Phone *"
                                            control={control}
                                            labelStyle={labelStyle}
                                            rules={{
                                                required: true,
                                                pattern: {
                                                    value: /^[0-9]{5,15}$/,
                                                    message: "Phone number must be between 5 and 15 digits",
                                                },
                                                maxLength: {
                                                    value: 15,
                                                    message: `Cannot exceed 15 digits`,
                                                }
                                            }}
                                        >
                                            <input placeholder="e.g. 9876543210" autoComplete="off" />
                                        </RFG>
                                    </Box>
                                </Box>
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2.5}>
                                <RFG name="username" label="Email *" control={control} labelStyle={labelStyle} rules={{
                                    required: true, maxLength: 500,
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i,
                                        message: "Enter a valid email username",
                                    }
                                }}>
                                    <input autoComplete="off" placeholder="e.g. john.doe@company.com" />
                                </RFG>
                                <RFG name="group" label="Main Role *" control={control} labelStyle={labelStyle} rules={{}} error={errors.project?.message}>
                                    <MultiSelect
                                        multiple={false}
                                        options={(roles || []).map((role) => ({ id: role.name, label: role.name }))}
                                        value={watch("group") || initialRole}
                                        defaultValue={initialRole}
                                        onValueChange={(value) => {
                                            console.log("value", value)
                                            setValue("group", value, { shouldValidate: true });
                                            if (value && value.length) clearErrors("role");
                                        }}
                                    />
                                </RFG>
                                <Box sx={{ pt: 1 }} display="flex">
                                    <RFG name="is_active" label="Active Account Status" control={control} labelStyle={labelStyle}>
                                        <input
                                            type="checkbox"
                                            onChange={(e) => setValue("is_active", e.target.checked)}
                                            checked={Boolean(watch("is_active"))}
                                        />
                                    </RFG>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </FormSectionCard>

                <Box display="flex" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
                    {showAutoFill && (
                        <Button
                            data-testid="btn-auto-fill-fake-bottom"
                            type="button"
                            variant="outlined"
                            onClick={handleFillFakeData}
                            sx={{
                                borderColor: '#10b981',
                                color: '#059669',
                                bgcolor: '#ecfdf5',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '13px',
                                borderRadius: '20px',
                                px: 2.5, py: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.15)',
                                '&:hover': { bgcolor: '#d1fae5', borderColor: '#059669', transform: 'translateY(-1px)' }
                            }}
                        >
                            ⚡ Auto-Fill Sample Data
                        </Button>
                    )}
                </Box>

                <FormFooter 
                    onCancel={back} 
                    onSubmitClick={(e) => setValue("action", e.target.innerText || (defaultValues?.id ? "Update" : "Save"))}
                    backText="Back" 
                    submitText={defaultValues?.id ? "Update User" : "Save User"} 
                    isEdit={!!defaultValues?.id}
                    backTestId="back-user-btn" 
                    saveTestId="save-user-btn" 
                />
            </form>
        </Box>
    );
}
